import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Search, Filter, Download, RefreshCw, Eye,
  Edit, Trash2, CheckCircle, XCircle, Calendar, User,
  ShoppingBag, IndianRupee, Truck, Clock, MoreVertical,
  ChevronDown, ChevronUp, ArrowUpDown, Printer, Mail,
  MapPin, Phone, CreditCard, AlertCircle, CheckCircle2,
  XCircle as XCircleIcon, Loader2, ExternalLink
} from 'lucide-react';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
  const [stats, setStats] = useState({
    total_orders: 0,
    pending_orders: 0,
    processing_orders: 0,
    shipped_orders: 0,
    delivered_orders: 0,
    cancelled_orders: 0,
    total_revenue: 0,
    today_orders: 0,
    avg_order_value: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const API_BASE = 'https://api.maechi.in';

  // Date formatting functions
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateOnly(dateString);
  };

  // Fetch orders
  const fetchOrders = useCallback(async (page = pagination.page) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page,
        limit: pagination.limit,
        ...(searchQuery && { search: searchQuery }),
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
        ...(selectedPaymentStatus !== 'all' && { payment_status: selectedPaymentStatus }),
        ...(dateRange.from && { date_from: dateRange.from }),
        ...(dateRange.to && { date_to: dateRange.to })
      });

      const response = await fetch(`${API_BASE}/get-all-orders.php?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setOrders(data.data || []);
        setPagination(data.pagination || pagination);
        setStats(data.stats || stats);
      } else {
        setError(data.message || 'Failed to fetch orders');
      }
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedStatus, selectedPaymentStatus, dateRange, pagination.limit]);

  // Initial load
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchOrders(1);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedStatus, selectedPaymentStatus, dateRange]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchOrders(newPage);
  };

  // Handle order status update
  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/update-order-status.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          status: newStatus
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setOrders(orders.map(order => 
          order.id === orderId ? { ...order, order_status: newStatus } : order
        ));
        setSuccessMessage(`Order status updated to ${newStatus}`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchOrders(); // Refresh stats
      } else {
        setError(data.message || 'Failed to update order status');
      }
    } catch (err) {
      console.error('Update Error:', err);
      setError('Failed to update order status');
    }
  };

  // Handle delete order
  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete order ${orderNumber}?`)) return;
    
    try {
      const response = await fetch(`${API_BASE}/delete-order.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_id: orderId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setOrders(orders.filter(order => order.id !== orderId));
        setSuccessMessage(`Order ${orderNumber} deleted successfully`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchOrders(); // Refresh stats
      } else {
        setError(data.message || 'Failed to delete order');
      }
    } catch (err) {
      console.error('Delete Error:', err);
      setError('Failed to delete order');
    }
  };

  // Toggle order selection
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Select all orders
  const selectAllOrders = () => {
    if (selectedOrders.length === orders.length && orders.length > 0) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(order => order.id));
    }
  };

  // Bulk actions
  const handleBulkStatusUpdate = (newStatus) => {
    if (selectedOrders.length === 0) {
      alert('Please select orders first');
      return;
    }
    
    if (!window.confirm(`Update ${selectedOrders.length} orders to ${newStatus}?`)) return;
    
    // Implement bulk update API call here
    console.log('Bulk update:', selectedOrders, 'to', newStatus);
    // For now, update locally
    selectedOrders.forEach(orderId => {
      handleStatusUpdate(orderId, newStatus);
    });
    setSelectedOrders([]);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { color: 'bg-blue-100 text-blue-800', icon: Loader2 },
      shipped: { color: 'bg-purple-100 text-purple-800', icon: Truck },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircleIcon }
    };
    
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: Package };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} className="mr-1.5" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Get payment status badge
  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircleIcon },
      refunded: { color: 'bg-gray-100 text-gray-800', icon: CreditCard }
    };
    
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: CreditCard };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} className="mr-1.5" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Loading state
  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Package className="mr-3" size={28} />
            Orders Management
          </h1>
          <p className="text-gray-600 mt-1">View and manage all customer orders</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => fetchOrders()}
            disabled={loading}
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center transition-colors">
            <Download size={18} className="mr-2" />
            Export
          </button>
          <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-colors">
            <Printer size={18} className="mr-2" />
            Print Report
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_orders || 0}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <ShoppingBag className="text-blue-600" size={24} />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Today's Orders</span>
              <span className="font-semibold text-gray-900">{stats.today_orders || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(stats.total_revenue || 0)}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <IndianRupee className="text-green-600" size={24} />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Avg. Order Value</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(stats.avg_order_value || 0)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Pending Orders</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.pending_orders || 0}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <Clock className="text-yellow-600" size={24} />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Processing</span>
              <span className="font-semibold text-gray-900">{stats.processing_orders || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Delivered Orders</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.delivered_orders || 0}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <Truck className="text-purple-600" size={24} />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Shipped</span>
              <span className="font-semibold text-gray-900">{stats.shipped_orders || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls and Filters */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-4">
        {/* Search and Filters Row */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="relative w-full lg:w-auto lg:flex-1 max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search orders by order number, customer name, email..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <select
              className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Order Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            <select
              className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedPaymentStatus}
              onChange={(e) => setSelectedPaymentStatus(e.target.value)}
            >
              <option value="all">All Payment Status</option>
              <option value="pending">Payment Pending</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
            
            <button className="px-4 py-2.5 border border-gray-300 rounded-lg flex items-center hover:bg-gray-50 transition-colors">
              <Filter size={18} className="mr-2" />
              More Filters
            </button>
          </div>
        </div>

        {/* Date Range and Bulk Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">From:</span>
              <input
                type="date"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">To:</span>
              <input
                type="date"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              />
            </div>
          </div>
          
          {selectedOrders.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">{selectedOrders.length} selected</span>
              <select
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                onChange={(e) => handleBulkStatusUpdate(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>Bulk Actions</option>
                <option value="processing">Mark as Processing</option>
                <option value="shipped">Mark as Shipped</option>
                <option value="delivered">Mark as Delivered</option>
                <option value="cancelled">Mark as Cancelled</option>
              </select>
              <button
                onClick={() => setSelectedOrders([])}
                className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                Clear Selection
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center animate-in slide-in-from-top">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
          <span className="text-green-800">{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage('')} 
            className="ml-auto hover:bg-green-100 p-1 rounded-full transition-colors"
          >
            <XCircle className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center animate-in slide-in-from-top">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
          <button 
            onClick={() => setError('')} 
            className="ml-auto hover:bg-red-100 p-1 rounded-full transition-colors"
          >
            <XCircle className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {orders.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="mx-auto text-gray-300" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mt-4">No orders found</h3>
            <p className="text-gray-600 mt-2">Try changing your search or filter criteria</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-4 w-12">
                    <input
                      type="checkbox"
                      checked={selectedOrders.length === orders.length && orders.length > 0}
                      onChange={selectAllOrders}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Order</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Customer</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Amount</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Payment</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <React.Fragment key={order.id}>
                    <tr className={`border-t border-gray-100 hover:bg-gray-50 transition-colors ${
                      expandedOrder === order.id ? 'bg-blue-50' : ''
                    }`}>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => toggleOrderSelection(order.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="p-4">
                        <div>
                          <div className="font-medium text-gray-900">{order.order_number}</div>
                          <div className="text-sm text-gray-500 mt-1 flex items-center">
                            <Package size={14} className="mr-1.5" />
                            {order.item_count || 0} items
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <div className="font-medium text-gray-900">{order.customer?.name || 'N/A'}</div>
                          <div className="text-sm text-gray-500 mt-1">{order.customer?.email || ''}</div>
                          <div className="text-sm text-gray-500">{order.customer?.phone || ''}</div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-bold text-gray-900">{formatCurrency(order.total_amount || 0)}</div>
                      </td>
                      <td className="p-4">
                        <div className="mb-2">
                          {getStatusBadge(order.order_status)}
                        </div>
                        <select
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          value={order.order_status || 'pending'}
                          onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="p-4">
                        {getPaymentStatusBadge(order.payment_status || 'pending')}
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-gray-900">{formatDateTime(order.created_at)}</div>
                        <div className="text-xs text-gray-500">{getTimeAgo(order.created_at)}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            {expandedOrder === order.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </button>
                          <button
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="View Order"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Send Email"
                          >
                            <Mail size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order.id, order.order_number)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Order"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Order Details */}
                    {expandedOrder === order.id && (
                      <tr>
                        <td colSpan="8" className="p-4 bg-blue-50 border-t border-blue-100">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Order Details */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <Package size={16} className="mr-2" />
                                Order Details
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Order Number:</span>
                                  <span className="font-medium">{order.order_number}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Transaction ID:</span>
                                  <span className="font-medium">{order.transaction_id || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Payment Method:</span>
                                  <span className="font-medium capitalize">{order.payment_method || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Items:</span>
                                  <span className="font-medium">{order.item_count || 0} items</span>
                                </div>
                                {order.item_names && (
                                  <div>
                                    <span className="text-gray-600">Products:</span>
                                    <p className="text-gray-900 mt-1 text-sm">{order.item_names}</p>
                                  </div>
                                )}
                                {order.notes && (
                                  <div>
                                    <span className="text-gray-600">Notes:</span>
                                    <p className="text-gray-900 mt-1 italic text-sm">{order.notes}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Shipping Address */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <MapPin size={16} className="mr-2" />
                                Shipping Address
                              </h4>
                              {order.shipping_address && typeof order.shipping_address === 'object' ? (
                                <div className="space-y-2 text-sm">
                                  <div className="font-medium">{order.shipping_address.full_name || 'N/A'}</div>
                                  <div>{order.shipping_address.address_line1 || ''}</div>
                                  {order.shipping_address.address_line2 && (
                                    <div>{order.shipping_address.address_line2}</div>
                                  )}
                                  <div>
                                    {order.shipping_address.city || ''}, {order.shipping_address.state || ''}
                                  </div>
                                  <div>{order.shipping_address.country || ''} - {order.shipping_address.postal_code || ''}</div>
                                  <div className="flex items-center mt-2">
                                    <Phone size={14} className="mr-2 text-gray-400" />
                                    {order.shipping_address.phone || 'N/A'}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-gray-500 text-sm">No shipping address provided</p>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h4 className="font-medium text-gray-900 mb-3">Quick Actions</h4>
                              <div className="space-y-2">
                                <button 
                                  onClick={() => handleStatusUpdate(order.id, 'delivered')}
                                  className="w-full flex items-center justify-center px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                >
                                  <CheckCircle size={16} className="mr-2" />
                                  Mark as Delivered
                                </button>
                                <button className="w-full flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                  <Printer size={16} className="mr-2" />
                                  Print Invoice
                                </button>
                                <button className="w-full flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                  <ExternalLink size={16} className="mr-2" />
                                  View Full Details
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {orders.length > 0 && pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between bg-white p-4 rounded-xl border border-gray-200 gap-4">
          <div className="text-sm text-gray-600">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} orders
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                
                if (pageNum > 0 && pageNum <= pagination.totalPages) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg text-sm ${
                        pagination.page === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                      } transition-colors`}
                      disabled={loading}
                    >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}
            </div>
            
            <button 
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;